import Honeybadger from 'honeybadger-js';
import React, { useMemo } from 'react';

import { useSendAddressCollectionEmailMutation } from '../../../generated/graphql';
import { getChangedValues } from '../../../helpers/getChangedValues';
import { findHouseholdsforAddrsCollection } from '../../../helpers/households';
import { editAddressCollectionEmailRoute, editAddressCollectionPath } from '../../../helpers/routes';
import AddressHouseholdRail from '../../SharePage/AddressHouseholdRail';
import EmailSequence from '../../SharePage/EmailSequence';
import { emailSharingSchema } from '../../SharePage/sharingValidation';
import useShareTracking from '../../SharePage/useShareTracking';
import { EmailPreviewContainer } from '../EmailPreviewContainer';
import { useFilteredHouseholds } from '../GuestFilters/useFilteredHouseholds';
import EmailEditorRail from './ShareWebsiteEmail/EmailEditorRail';
import { useSequenceState } from './useSequenceState';

export interface ShareValues {
  subject: string;
  message: string;
  linkButtonText: string;
}

const AddressCollectionEmail: React.FC = () => {
  const [sendEmail, { loading: sendingMessage }] = useSendAddressCollectionEmailMutation();

  const {
    sequence,
    wwsLoading,
    guestIds,
    setGuestIds,
    setHideRecaptcha,
    hideRecaptcha,
    dispatch,
    recaptchaRef,
    seqId,
    showError,
    errorMessage,
    showSuccess,
    householdLoading,
  } = useSequenceState();

  const { filteredHouseholds, setFilterCriteria } = useFilteredHouseholds();
  const households = useMemo(() => findHouseholdsforAddrsCollection('email', filteredHouseholds), [filteredHouseholds]);
  const loading = wwsLoading || sendingMessage;
  const track = useShareTracking();

  const message = sequence?.messagingSequence?.defaultEmailBody ?? '';

  let initialValues: ShareValues = {
    subject: sequence?.messagingSequence?.defaultEmailSubject ?? '',
    message,
    linkButtonText: sequence?.messagingSequence?.defaultLinkText ?? '',
  };

  const sendMessage = async (values: ShareValues) => {
    if (loading) {
      return;
    }

    const edited = getChangedValues(initialValues, values);

    track('Website Shared', {
      guestMessagedCount: guestIds.length,
      message: values.message,
      messageCustomized: Boolean(Object.keys(edited).length),
      messageType: sequence?.messagingSequence?.displayName,
      medium: 'email',
    });

    dispatch({ type: 'RESET', payload: { hideRecaptcha: true, showSuccess: false, showError: false } });

    try {
      // Convert all newlines in the message body into <br /> tags.
      const convertedMessage = values.message.replace(/\n/g, '<br/>');
      await sendEmail({
        variables: {
          args: {
            subject: values.subject,
            body: convertedMessage,
            linkButtonText: values.linkButtonText,
            guestIds,
            token: recaptchaRef.current?.getValue() || '',
          },
        },
      });
      dispatch({ type: 'SUCCESS' });
    } catch (error) {
      Honeybadger.notify(error, 'Error sending addresss collection email', { context: { error } });
      dispatch({ type: 'ERROR', payload: { message: error.message } });
    } finally {
      recaptchaRef.current?.reset();
    }
  };

  const editRoute = editAddressCollectionEmailRoute(seqId);

  return (
    <EmailSequence
      loading={loading}
      showError={showError}
      errorMessage={errorMessage}
      showSuccess={showSuccess}
      initialValues={initialValues}
      onSend={sendMessage}
      PreviewComponent={
        <EmailPreviewContainer
          editRoute={editRoute}
          matchPath={editAddressCollectionPath}
          displayName={sequence?.messagingSequence?.displayName}
        />
      }
      EditorComponent={
        <EmailEditorRail
          matchPath={editAddressCollectionPath}
          displayName={sequence?.messagingSequence?.displayName}
          initialValues={initialValues}
        />
      }
      HouseholdRailComponent={AddressHouseholdRail}
      hideRecaptcha={hideRecaptcha}
      setHideRecaptcha={setHideRecaptcha}
      recaptchaRef={recaptchaRef}
      setGuestIds={setGuestIds}
      editPath={editAddressCollectionPath}
      editRoute={editRoute}
      validationSchema={emailSharingSchema}
      households={households}
      householdLoading={householdLoading}
      setFilter={setFilterCriteria}
      displayName={sequence?.messagingSequence?.displayName}
    />
  );
};

export default AddressCollectionEmail;
